import { useMemo } from "react";
import { format, isSameDay } from "date-fns";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { SlotOverlayIndicators } from "./SlotOverlayIndicators";
import { AvatarStack } from "../../../components/ui/voter-avatars";
import {
  formatZonedTime,
  formatZonedTimeRange,
  toDisplayDate,
  durationMinutes,
  toUtcMs,
} from "../../../lib/time";
import { enumerateOverlappedDays, localDayKey } from "../../../lib/days";
import { earliestDisplayStart } from "../utils/initial-calendar-month";
import { buildCreatorAgendaOverlayLane } from "../utils/creator-agenda-overlays";

// Availability-lane badge styling, mirrored from MobileAvailabilityAgenda so the
// two mobile agendas read consistently (audit A-4c overlay lane styling).
const LANE_BADGE = {
  preferred:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  feasible: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  busy: "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
};
const LANE_LABEL = { preferred: "Preferred", feasible: "Feasible", busy: "Busy" };

// Tap-friendly, day-grouped list replacement for the react-big-calendar grid
// on phones. On a ~393px viewport the month/week grid is unreadable and its
// cells are too small to tap reliably; this surface lists the creator's own
// candidate slots chronologically per day with explicit Add / Edit / Remove
// affordances. Per-day SlotOverlayIndicators mirror the desktop slot list so
// friend-availability context (conflict / preferred / feasible) stays visible
// next to each candidate time.
//
// Mobile-only — the desktop create/edit page renders the calendar grid
// unchanged. Quick Add is intentionally ignored here (it stays a
// calendar-surface behavior); the agenda always uses explicit actions.

// Shared focus ring so every interactive row meets the visible-focus a11y
// rule (mirrors MobileAvailabilityAgenda's brand focus token).
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
const ROW_OUTER = "rounded-xl border";
const ROW_INNER =
  "flex min-h-[44px] w-full items-center gap-3 px-3 py-2 text-left";

export function CreatorSlotAgenda({
  slots = [],
  date = new Date(),
  onNavigate = () => {},
  effectiveTimezone,
  invalidSlotIds,
  // calendarEvents carries the same overlay feed the desktop calendar/slot
  // list render (friend + self marks/busy windows). The per-slot indicator
  // badges below are fed by the precomputed slotIndicatorsBySlotId map (same
  // source as CreateSchedulerPage's slot list) so the two surfaces never
  // disagree; calendarEvents is used here to surface a lightweight "friend
  // availability is overlaid this month" hint in the month header.
  calendarEvents = [],
  slotIndicatorsBySlotId = {},
  tooltipIdentifierByOwnerId = {},
  // Edit-mode vote context. When editing an existing poll the desktop slot list
  // shows per-slot Preferred/Feasible voter stacks + counts; the agenda mirrors
  // that here so switching to the mobile agenda surface doesn't drop the
  // at-a-glance vote signal (it is the whole point of the edit view).
  isEditing = false,
  slotVotersBySlotId = {},
  talliesBySlotId = {},
  colorMap = {},
  onAvatarClick,
  onAddSlot = () => {},
  onAddSlotForDay = () => {},
  onEditSlot = () => {},
  onRemoveSlot = () => {},
}) {
  const invalidSet =
    invalidSlotIds instanceof Set
      ? invalidSlotIds
      : new Set(Array.isArray(invalidSlotIds) ? invalidSlotIds : []);

  // Scope the list to the DISPLAYED month (matching MobileAvailabilityAgenda)
  // so the month header + prev/next nav stay meaningful. Group by display day
  // in the effective timezone. Past slots are kept and flagged invalid rather
  // than dropped, so the creator can see and remove them before saving.
  const groups = useMemo(() => {
    const byDay = new Map();
    for (const slot of slots) {
      if (!slot?.id || !slot.start) continue;
      const rawStart = slot.start instanceof Date ? slot.start : new Date(slot.start);
      if (Number.isNaN(rawStart.getTime())) continue;
      const displayStart = toDisplayDate(rawStart, effectiveTimezone);
      if (!displayStart) continue;
      if (
        displayStart.getFullYear() !== date.getFullYear() ||
        displayStart.getMonth() !== date.getMonth()
      )
        continue;
      const key = localDayKey(displayStart);
      if (!byDay.has(key)) {
        byDay.set(key, { key, date: displayStart, slots: [] });
      }
      byDay.get(key).slots.push(slot);
    }
    return [...byDay.values()]
      .map((group) => ({
        ...group,
        slots: group.slots
          .slice()
          .sort((a, b) => toUtcMs(a.start) - toUtcMs(b.start)),
      }))
      .sort((a, b) => a.date - b.date);
  }, [slots, date, effectiveTimezone]);

  const monthLabel = format(date, "MMMM yyyy");
  // When the displayed month has no slots but the poll DOES have slots in other
  // months, point the creator at the soonest one (audit A-2: this used to read
  // as a false "No slots proposed yet"). Computed on the SAME display-date basis
  // as `groups` so the month it names always matches where the slots render.
  const otherMonthTarget = useMemo(() => {
    const displayStarts = slots
      .map((slot) => {
        const raw =
          slot?.start instanceof Date
            ? slot.start
            : slot?.start
              ? new Date(slot.start)
              : null;
        if (!raw || Number.isNaN(raw.getTime())) return null;
        return toDisplayDate(raw, effectiveTimezone) || raw;
      })
      .filter(Boolean);
    return earliestDisplayStart(displayStarts);
  }, [slots, effectiveTimezone]);
  // Count distinct non-self overlay owners with marks/busy windows falling in
  // the displayed month, so the month header can signal that friend
  // availability context is present (the per-slot badges carry the detail).
  const friendOverlayCount = useMemo(() => {
    if (!calendarEvents.length) return 0;
    const ownerIds = new Set();
    for (const evt of calendarEvents) {
      const r = evt?.resource;
      if (!r) continue;
      if (r.kind !== "mark" && r.kind !== "overlay-busy") continue;
      if (r.isSelf) continue;
      const rawStart = evt.start instanceof Date ? evt.start : null;
      if (!rawStart) continue;
      // Scope by the display date in the effective timezone, matching the
      // per-day `groups` scoping above so the header badge and the listed days
      // never disagree about which month an event belongs to near a boundary.
      const start = toDisplayDate(rawStart, effectiveTimezone) || rawStart;
      if (
        start.getFullYear() !== date.getFullYear() ||
        start.getMonth() !== date.getMonth()
      )
        continue;
      if (r.ownerId) ownerIds.add(r.ownerId);
    }
    return ownerIds.size;
  }, [calendarEvents, date, effectiveTimezone]);
  // Per-day availability lane (audit A-3): the mobile echo of the desktop
  // calendar's busy/mark overlay tiles, so a phone creator can see their own
  // (and overlaid friends') availability next to each day's candidate slots.
  // Keyed by the SAME local-day key as `groups`, so the lane lines up under the
  // right slot day. Read-only context — authoring stays in the slot rows.
  const overlayLaneByDay = useMemo(
    () =>
      buildCreatorAgendaOverlayLane({
        events: calendarEvents,
        date,
        timeZone: effectiveTimezone,
      }),
    [calendarEvents, date, effectiveTimezone],
  );
  const goPrev = () => {
    const d = new Date(date);
    d.setMonth(d.getMonth() - 1);
    onNavigate(d);
  };
  const goNext = () => {
    const d = new Date(date);
    d.setMonth(d.getMonth() + 1);
    onNavigate(d);
  };

  return (
    <div data-testid="scheduler-mobile-slot-agenda" className="space-y-4">
      <div className="flex items-center justify-center gap-1">
        <button
          type="button"
          onClick={goPrev}
          aria-label="Previous month"
          className={`inline-flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 ${FOCUS_RING}`}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-[8.5rem] text-center text-sm font-semibold tabular-nums">
          {monthLabel}
          {friendOverlayCount > 0 && (
            <span
              className="ml-1.5 inline-flex items-center rounded-full bg-brand-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-brand-primary dark:bg-brand-primary/20 dark:text-blue-300"
              aria-label={`${friendOverlayCount} friend${friendOverlayCount === 1 ? "" : "s"} sharing availability this month`}
            >
              {friendOverlayCount}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={goNext}
          aria-label="Next month"
          className={`inline-flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 ${FOCUS_RING}`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {groups.length === 0 ? (
        otherMonthTarget ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              No slots in {monthLabel}
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              This poll&rsquo;s slots are in{" "}
              {format(otherMonthTarget, "MMMM yyyy")}.
            </p>
            <button
              type="button"
              onClick={() => onNavigate(otherMonthTarget)}
              data-testid="creator-agenda-other-month"
              className={`mt-4 inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-brand-primary px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary/90 ${FOCUS_RING}`}
            >
              Go to {format(otherMonthTarget, "MMMM")}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              No slots proposed yet
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Add a candidate time so friends can vote on it.
            </p>
            <button
              type="button"
              onClick={onAddSlot}
              data-testid="creator-agenda-empty-add"
              className={`mt-4 inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-brand-primary px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary/90 ${FOCUS_RING}`}
            >
              <Plus className="h-3.5 w-3.5" /> Add slot
            </button>
          </div>
        )
      ) : (
        groups.map((group) => {
          const dayLabel = format(group.date, "EEE, MMM d");
          const isToday = isSameDay(group.date, new Date());
          return (
            <div key={group.key}>
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {isToday ? "Today · " : ""}
                  {dayLabel}
                </p>
                <button
                  type="button"
                  onClick={() => onAddSlotForDay(group.date)}
                  aria-label={`Add slot on ${dayLabel}`}
                  className={`inline-flex min-h-[44px] items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:border-brand-primary/40 hover:bg-brand-primary/10 hover:text-brand-primary dark:border-slate-700 dark:text-slate-300 dark:hover:border-brand-primary/50 dark:hover:bg-brand-primary/20 dark:hover:text-blue-300 ${FOCUS_RING}`}
                >
                  <Plus className="h-3 w-3" /> Add
                </button>
              </div>
              <div className="space-y-2">
                {group.slots.map((slot) => {
                  const isInvalid = invalidSet.has(slot.id);
                  const indicators = slotIndicatorsBySlotId[slot.id];
                  const timeRange = formatZonedTimeRange({
                    start: slot.start,
                    end: slot.end,
                    timeZone: effectiveTimezone,
                    showTimeZone: false,
                  });
                  const mins = durationMinutes(slot.start, slot.end);
                  // A slot that runs past local midnight gets a compact "Ends …"
                  // tail hint (no fan-out onto the next day — the creator
                  // authored this slot and a duplicate row would read as a
                  // separate candidate).
                  const slotStartDate =
                    slot.start instanceof Date ? slot.start : new Date(slot.start);
                  const slotEndDate = slot.end
                    ? slot.end instanceof Date
                      ? slot.end
                      : new Date(slot.end)
                    : null;
                  const crossesMidnight =
                    slotEndDate &&
                    enumerateOverlappedDays({
                      start: slotStartDate,
                      end: slotEndDate,
                      timeZone: effectiveTimezone,
                    }).length > 1;
                  const endsHint = crossesMidnight
                    ? `, ends ${formatZonedTime(slotEndDate, effectiveTimezone, "EEE h:mm a", { showTimeZone: false })}`
                    : "";
                  // Fold the cross-midnight cue into the Edit button's accessible
                  // name so a screen-reader user tabbing controls hears it too
                  // (the visible "Ends …" hint is separate adjacent text).
                  const rowAriaLabel = `Edit slot ${timeRange}${mins ? `, ${mins} minutes` : ""}${endsHint}`;
                  const voters = slotVotersBySlotId[slot.id] || {};
                  const tally = talliesBySlotId[slot.id] || {};
                  return (
                    <div
                      key={slot.id}
                      data-testid={`creator-slot-row-${slot.id}`}
                      className={`${ROW_OUTER} ${
                        isInvalid
                          ? "border-red-300 bg-red-50/60 dark:border-red-700 dark:bg-red-900/20"
                          : "border-slate-200/70 bg-white dark:border-slate-700 dark:bg-slate-800"
                      }`}
                    >
                      <div className={ROW_INNER}>
                        {/* Plain (non-button) info block: the overlay indicators
                            are themselves popover-trigger buttons, so they must
                            NOT be nested inside an outer row button. Edit is an
                            explicit action button alongside Remove. */}
                        <div className="flex min-w-0 flex-1 flex-col items-start">
                          <span className="flex w-full items-center text-sm font-semibold text-slate-900 dark:text-slate-100">
                            <span className="truncate">{timeRange}</span>
                            <SlotOverlayIndicators
                              conflict={indicators?.conflict || []}
                              preferred={indicators?.preferred || []}
                              feasible={indicators?.feasible || []}
                              identifierByOwnerId={tooltipIdentifierByOwnerId}
                            />
                          </span>
                          <span className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            Duration {mins} min
                          </span>
                          {crossesMidnight && (
                            <span className="mt-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                              Ends{" "}
                              {formatZonedTime(slotEndDate, effectiveTimezone, "EEE h:mm a", {
                                showTimeZone: false,
                              })}
                            </span>
                          )}
                          {isInvalid && (
                            <span className="mt-1 text-[11px] font-semibold text-red-500 dark:text-red-400">
                              In the past — remove to save
                            </span>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <button
                            type="button"
                            onClick={() => onEditSlot(slot.id)}
                            aria-label={rowAriaLabel}
                            className={`inline-flex min-h-[44px] items-center rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-brand-primary/40 hover:bg-brand-primary/10 hover:text-brand-primary dark:border-slate-700 dark:text-slate-300 dark:hover:border-brand-primary/50 dark:hover:bg-brand-primary/20 dark:hover:text-blue-300 ${FOCUS_RING}`}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => onRemoveSlot(slot.id)}
                            aria-label={`Remove slot ${timeRange}`}
                            className={`inline-flex min-h-[44px] items-center rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 dark:border-slate-700 dark:text-slate-400 dark:hover:border-red-800 dark:hover:bg-red-900/30 dark:hover:text-red-300 ${FOCUS_RING}`}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      {/* Edit-mode vote tallies — kept OUTSIDE the row's edit
                          button so the AvatarStack's own click targets aren't
                          nested interactive elements. Mirrors the desktop slot
                          list's Preferred/Feasible stacks. */}
                      {isEditing && (
                        <div
                          data-testid={`creator-slot-votes-${slot.id}`}
                          className="space-y-1.5 border-t border-slate-100 px-3 py-2 dark:border-slate-700/60"
                        >
                          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            <span className="font-semibold">★ Preferred</span>
                            <AvatarStack
                              users={voters.preferred || []}
                              max={6}
                              size={20}
                              colorMap={colorMap}
                              onClick={onAvatarClick}
                            />
                            <span className="text-slate-400 dark:text-slate-500">
                              {tally.preferred || 0}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            <span className="font-semibold">✓ Feasible</span>
                            <AvatarStack
                              users={voters.feasible || []}
                              max={6}
                              size={20}
                              colorMap={colorMap}
                              onClick={onAvatarClick}
                            />
                            <span className="text-slate-400 dark:text-slate-500">
                              {tally.feasible || 0}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {(() => {
                const laneRows = overlayLaneByDay.get(group.key);
                if (!laneRows || laneRows.length === 0) return null;
                return (
                  <div
                    data-testid={`creator-agenda-overlay-${group.key}`}
                    className="mt-2 space-y-1.5 rounded-xl border border-dashed border-slate-200/80 bg-slate-50/60 p-2 dark:border-slate-700/70 dark:bg-slate-900/30"
                  >
                    <p className="px-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Availability
                    </p>
                    {laneRows.map((row) => {
                      const ownerLabel = row.isSelf
                        ? "You"
                        : tooltipIdentifierByOwnerId[row.ownerId] || "A friend";
                      const timeLabel = row.isAllDay
                        ? "All day"
                        : formatZonedTimeRange({
                            start: row.clippedStart,
                            end: row.clippedEnd,
                            timeZone: effectiveTimezone,
                            showTimeZone: false,
                          });
                      return (
                        <div
                          key={row.id}
                          // Day-key prefix keeps the testid unique when one
                          // multi-day span fans onto several days (it reuses the
                          // original event id on each day) — Playwright's
                          // getByTestId expects a single match.
                          data-testid={`creator-overlay-row-${group.key}-${row.id}`}
                          className="flex min-h-[28px] items-center gap-2 px-1 text-xs"
                        >
                          <span className="min-w-0 flex-1 truncate text-slate-600 dark:text-slate-300">
                            <span className="font-medium text-slate-700 dark:text-slate-200">
                              {ownerLabel}
                            </span>
                            <span className="text-slate-400 dark:text-slate-500">
                              {" · "}
                              {timeLabel}
                              {row.isContinuation ? " (cont.)" : ""}
                            </span>
                          </span>
                          <span
                            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${LANE_BADGE[row.kind]}`}
                          >
                            {LANE_LABEL[row.kind]}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          );
        })
      )}
    </div>
  );
}
